import { useAppDispatch } from '@hooks/reduxHooks'
import { CreditNoteProposalStatus, ProductOrderDto, SupplierContactType } from '@services/dtos'
import ProductOrderDiscrepanciesInfo from './ProductOrderDiscrepanciesInfo'
import { Button, Typography } from '@mui/material'
import SelectSupplierEmail from './SelectSupplierEmail'
import { useForm } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'
import { creditNoteChoiceSchema } from '@utils/schemas'
import { getProductOrders, saveCreditNoteChoice } from '@state/reducers/productOrderReducer'
import { enqueueSnackbar } from '@state/reducers/alertReducer'
import { useState } from 'react'

interface Props {
  order: ProductOrderDto
  onDismiss?: () => void
}

export const ProductOrderSendingCreditNote = ({ order, onDismiss }: Props) => {
  const { control, handleSubmit } = useForm<{ supplierContactIds: string[] }>({
    resolver: yupResolver(creditNoteChoiceSchema),
    defaultValues: {
      supplierContactIds: []
    }
  })
  const [isSubmitting, setIsSubmitting] = useState(false)
  const dispatch = useAppDispatch()

  const cb = async (msg: string) => {
    dispatch(
      enqueueSnackbar({
        message: msg,
        options: { variant: 'success' },
      }),
    )
  }

  const onSubmit = (data: { supplierContactIds: string[] }) => {
    setIsSubmitting(true)
    dispatch(saveCreditNoteChoice({
      id: order.id,
      dto: {
        choice: CreditNoteProposalStatus.ACCEPTED,
        ...data
      }
    }))
      .unwrap()
      .then(() => {
        cb('La demande d\'envoi d\'avoir a été enregistrée avec succès')
        dispatch(getProductOrders())
      })
      .catch(() => cb('Une erreur est survenue'))
      .finally(() => setIsSubmitting(false))
  }

  const decline = () => {
    setIsSubmitting(true)
    dispatch(saveCreditNoteChoice({ id: order.id, dto: { choice: CreditNoteProposalStatus.REFUSED }}))
      .unwrap()
      .then(() => {
        cb('La demande d\'envoi d\'avoir a été refusée')
        dispatch(getProductOrders())
      })
      .catch(() => cb('Une erreur est survenue'))
      .finally(() => setIsSubmitting(false))
  }

  return (
    <>
      <Typography variant="body1">{'Souhaitez-vous envoyer une demande d\'avoir au fournisseur ?'}</Typography>
      <SelectSupplierEmail
        supplier={order.supplier}
        control={control}
        multiple
        contactTypes={[SupplierContactType.COMMERCIAL, SupplierContactType.SALES_ADMINISTRATION]}
      />
      <ProductOrderDiscrepanciesInfo order={order} />
      <div className="flex justify-center gap-4 mt-4">
        <Button
          variant="text"
          size="small"
          color="inherit"
          onClick={onDismiss}>
          Plus tard
        </Button>
        <Button
          variant="outlined"
          size="small"
          onClick={decline}>
          Refuser
        </Button>
        <Button
          variant="contained"
          size="small"
          disabled={isSubmitting}
          onClick={handleSubmit(onSubmit)}>
          Accepter
        </Button>
      </div>
    </>
  )
}

export default ProductOrderSendingCreditNote
